// src/components/Dashboard/Dashboard.tsx (update existing component)
import React, { useState, useEffect } from "react";
import { Select, Spin, Alert, DatePicker, Radio } from "antd";
import SummaryCards from "./SummaryCards";
import FoldersByMonthChart from "./charts/FoldersByMonthChart";
import FoldersByUserChart from "./charts/FoldersByUserChart";
import StatusDistributionCharts from "./charts/StatusDistributionChart";
import MultireportAndContractCharts from "./charts/MultreportAndContractChart";
import FoldersByCollaboratorChart from "./charts/FoldersByCollaboratorChart";
import ValidatedRequirementsByUserChart from "./charts/ValidatedRequiermentsByUserChart";
import CompletionTimeByMonthChart from "./charts/CompletionTimeByMonthChart";
import CompletionTimeByUserChart from "./charts/CompletionTimeByUserChart";
import CompletionTimeCard from "./CompletionTimeCard";

import { format } from "date-fns";
import { SyncOutlined, DownloadOutlined } from "@ant-design/icons";
import api from "../../api";

const { Option } = Select;
const { RangePicker } = DatePicker;

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

const Dashboard: React.FC = () => {
  // Filter and state variables
  const [period, setPeriod] = useState<string>("month");
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [detailedData, setDetailedData] = useState<any>(null);
  const [filterType, setFilterType] = useState<"preset" | "custom">("preset");

  // Fetch user list for admin users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get("/metrics/users/");
        setUsers(response.data);
      } catch (err) {
        console.error("Error fetching users:", err);
        // Silently fail - likely due to permissions
        setUsers([]);
      }
    };

    fetchUsers();
  }, []);

  // Main fetch data function
  const fetchData = async () => {
    setLoading(true);
    try {
      // Prepare query parameters
      let params: Record<string, string> = {};

      if (filterType === "preset") {
        params.period = period;
      } else if (filterType === "custom" && dateRange) {
        params.start_date = dateRange[0];
        params.end_date = dateRange[1];
      }

      if (selectedUserId) {
        params.user_id = selectedUserId.toString();
      }

      // Construct query string
      const queryString = new URLSearchParams(params).toString();

      // Parallel API requests
      const [summaryResponse, detailedResponse] = await Promise.all([
        api.get(`/metrics/summary/?${queryString}`),
        api.get(`/metrics/detailed/?${queryString}`),
      ]);

      setSummaryData(summaryResponse.data);
      setDetailedData(detailedResponse.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching metrics:", err);
      setError("Failed to load metrics data. Please try again later.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch data on filter changes
  useEffect(() => {
    fetchData();
  }, [period, dateRange, selectedUserId, filterType]);

  // Event handlers
  const handlePeriodChange = (value: string) => {
    setPeriod(value);
  };

  const handleDateRangeChange = (
    _dates: any,
    dateStrings: [string, string],
  ) => {
    setDateRange(dateStrings);
  };

  const handleUserChange = (value: number | null) => {
    setSelectedUserId(value);
  };

  const handleFilterTypeChange = (e: any) => {
    setFilterType(e.target.value);
    // Reset other filters
    if (e.target.value === "preset") {
      setDateRange(null);
    } else {
      setPeriod("month"); // Default period
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const exportToCsv = () => {
    // Function to convert data to CSV format
    const convertToCSV = (objArray: any[], headerList: string[]) => {
      const array =
        typeof objArray !== "object" ? JSON.parse(objArray) : objArray;
      let str = headerList.join(",") + "\r\n";

      for (let i = 0; i < array.length; i++) {
        let line = "";
        for (const index in headerList) {
          const head = headerList[index];
          if (line !== "") line += ",";
          line += array[i][head] !== undefined ? array[i][head] : "";
        }
        str += line + "\r\n";
      }
      return str;
    };

    // Default to summary data export
    const summaryHeaders = ["metric", "value"];
    const summaryRows = [
      { metric: "Total Folders", value: summaryData?.total_folders || 0 },
      {
        metric: "In Progress Folders",
        value: summaryData?.in_progress_folders || 0,
      },
      {
        metric: "Completed Folders",
        value: summaryData?.completed_folders || 0,
      },
      { metric: "Active Folders", value: summaryData?.active_folders || 0 },
      { metric: "Inactive Folders", value: summaryData?.inactive_folders || 0 },
      {
        metric: "Multireport Delivered",
        value: summaryData?.multireport_delivered || 0,
      },
      {
        metric: "Lease Contract Generated",
        value: summaryData?.lease_contract_generated || 0,
      },
      {
        metric: "Avg Completion Time (Days)",
        value: summaryData?.avg_completion_time_days || 0,
      },
    ];
    const csvContent = convertToCSV(summaryRows, summaryHeaders);
    const filename = "metrics-data";

    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${filename}-${format(new Date(), "yyyy-MM-dd")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 md:mb-0"></h1>
          <div className="flex flex-wrap items-center gap-2">
            {/* Filter Controls */}
            <div className="flex flex-wrap items-center gap-2 mr-4">
              <Radio.Group
                onChange={handleFilterTypeChange}
                value={filterType}
                className="mr-2"
              >
                <Radio.Button value="preset">Predefinido</Radio.Button>
                <Radio.Button value="custom">Custom</Radio.Button>
              </Radio.Group>

              {filterType === "preset" ? (
                <Select
                  defaultValue="month"
                  className="w-32"
                  onChange={handlePeriodChange}
                  value={period}
                >
                  <Option value="week">Últimos 7 Días</Option>
                  <Option value="month">Últimos 30 Días</Option>
                  <Option value="quarter">Últimos 90 Días</Option>
                  <Option value="year">Último Año</Option>
                  <Option value="all">Todo el Tiempo</Option>
                </Select>
              ) : (
                <RangePicker
                  onChange={handleDateRangeChange}
                  className="w-auto"
                />
              )}

              {users.length > 0 && (
                <Select
                  className="w-48"
                  onChange={handleUserChange}
                  value={selectedUserId}
                  allowClear
                  placeholder="Todos los Usuarios"
                  showSearch
                  optionFilterProp="label"
                  options={users.map((user) => ({
                    value: user.id,
                    label:
                      `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                      user.email,
                  }))}
                  style={{ minWidth: "180px" }}
                  dropdownStyle={{ minWidth: "180px" }}
                  optionLabelProp="label"
                />
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleRefresh}
                disabled={loading || refreshing}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <SyncOutlined spin={refreshing} className="mr-1" />
                Refresh
              </button>
              <button
                onClick={exportToCsv}
                disabled={loading || !summaryData}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <DownloadOutlined className="mr-1" />
                Exportar
              </button>
            </div>
          </div>
        </div>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            className="mb-6"
          />
        )}

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <Spin size="large" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <SummaryCards data={summaryData} />

            {/* Completion Time Card */}
            <CompletionTimeCard
              avgCompletionDays={summaryData?.avg_completion_time_days || 0}
              totalCompleted={summaryData?.total_completed_with_history || 0}
              totalFolders={summaryData?.total_folders || 0}
            />

            {/* Folders by Month Chart */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-medium mb-4 text-gray-700">
                Folders Creados por Mes
              </h2>
              <FoldersByMonthChart
                data={detailedData?.folders_by_month || []}
                period={period}
                filterType={filterType}
                dateRange={dateRange}
              />
            </div>

            {/* Completion Time Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-4">
                <h2 className="text-lg font-medium mb-4 text-gray-700">
                  Tiempo Promedio Completado por Mes
                </h2>
                <CompletionTimeByMonthChart
                  data={detailedData?.completion_time_by_month || []}
                />
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <h2 className="text-lg font-medium mb-4 text-gray-700">
                  Tiempo Promedio Completado por Usuario
                </h2>
                <CompletionTimeByUserChart
                  data={detailedData?.completion_time_by_user || []}
                />
              </div>
            </div>

            {/* User Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-4">
                <h2 className="text-lg font-medium mb-4 text-gray-700">
                  Folders por Usuario Asignado (Ops)
                </h2>
                <FoldersByUserChart
                  data={detailedData?.folders_by_user || []}
                />
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <h2 className="text-lg font-medium mb-4 text-gray-700">
                  Folders por Colaborador (CJA)
                </h2>
                <FoldersByCollaboratorChart
                  data={detailedData?.folders_by_collaborator || []}
                />
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <h2 className="text-lg font-medium mb-4 text-gray-700">
                  Requisitos Validados por Usuario
                </h2>
                <ValidatedRequirementsByUserChart
                  data={detailedData?.validated_requirements_by_user || []}
                />
              </div>
            </div>

            {/* Status Distribution Charts */}
            <StatusDistributionCharts
              foldersData={summaryData?.folders_by_status || []}
              requirementsData={summaryData?.requirements_by_status || []}
            />

            {/* Multireport and Contract Charts */}
            <MultireportAndContractCharts
              multireportData={
                detailedData?.multireport_delivered_by_user || []
              }
              contractData={
                detailedData?.lease_contract_generated_by_user || []
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
